import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import PiBrowseListRow from "../../components/PiBrowseListRow";
import PiBrowseTile from "../../components/PiBrowseTile";
import useGridColumns from "../../hooks/useGridColumns";
import { InteractionDefintion } from "./InteractionDefinitions";
import { useInteractionPickerIcons } from "./InteractionPickerIconHooks";
import {
  DEFAULT_TILE_COLUMNS,
  InteractionPickerGroup,
  MAX_TILE_COLUMNS,
  MIN_TILE_WIDTH,
  TILE_GAP,
} from "./useInteractionPickerList";
import { BrowseDialogViewMode } from "../../app/slices/GlobalSlice";

interface InteractionPickerResultsProps {
  groups: InteractionPickerGroup[];
  viewMode: BrowseDialogViewMode;
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
  onConfirmIndex: (index: number) => void;
  /** Reports the measured column count so the arrow keys can step by a row. */
  onColumnsChange: (columns: number) => void;
  /**
   * Ties the results to the search field for assistive tech: the field's
   * `aria-activedescendant` points at one of these option ids.
   */
  listboxId: string;
  optionIdFor: (index: number) => string;
}

export default function InteractionPickerResults(
  props: InteractionPickerResultsProps
) {
  const {
    groups,
    viewMode,
    selectedIndex,
    onSelectIndex,
    onConfirmIndex,
    onColumnsChange,
    listboxId,
    optionIdFor,
  } = props;
  const { t } = useTranslation();
  const [getIcon] = useInteractionPickerIcons();
  // Shared by both renderers, so it holds the common base type: the tile is a
  // button, the list row a div. A callback ref rather than the object itself,
  // which would not be assignable to either element's narrower ref type.
  const selectedRef = useRef<HTMLElement | null>(null);
  const setSelectedRef = useCallback((node: HTMLElement | null) => {
    selectedRef.current = node;
  }, []);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const columns = useGridColumns(gridRef, {
    minCellWidth: MIN_TILE_WIDTH,
    gap: TILE_GAP,
    fallback: DEFAULT_TILE_COLUMNS,
    max: MAX_TILE_COLUMNS,
  });

  useEffect(() => {
    onColumnsChange(columns);
  }, [columns, onColumnsChange]);

  // The highlight can be moved by a key to somewhere well outside the scroll
  // window, so the window has to follow it.
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex, viewMode]);

  if (groups.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-[24px] text-[13px] text-creator-text-sub text-center">
        {t("interaction_picker_ui.no_results")}
      </div>
    );
  }

  const listboxProps = {
    id: listboxId,
    role: "listbox" as const,
    "aria-label": t("interaction_picker_ui.results_label"),
    className:
      "flex-1 overflow-y-auto pit-quiet-scrollbar py-[8px]",
  };

  const groupHeader = (group: InteractionPickerGroup, padded: boolean) => (
    <div
      className={
        (padded ? "px-[16px] " : "") +
        "pt-[10px] pb-[6px] text-[10px] font-semibold uppercase tracking-[0.08em] text-creator-text-sub"
      }
    >
      {group.label}
    </div>
  );

  const rowSubtitle = (definition: InteractionDefintion) =>
    t("interaction_descriptions." + definition.description);

  // Both views group the results; tiles render one grid per group under its
  // header. The arrow keys stay honest because navigateGroupedGrid knows the
  // same group geometry the renderer draws.
  if (viewMode === "tile") {
    return (
      <div {...listboxProps}>
        {/* The padding sits on the wrapper so the measured element's width is
            the width actually available to cells. */}
        <div className="px-[16px]">
          <div ref={gridRef}>
            {groups.map((group) => (
              <div key={group.key}>
                {groupHeader(group, false)}
                <div
                  className="grid"
                  style={{
                    gap: TILE_GAP,
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                  }}
                >
                  {group.items.map((definition, offset) => {
                    const index = group.startIndex + offset;
                    return (
                      <PiBrowseTile
                        key={`${group.key}-${definition.name}`}
                        ref={index === selectedIndex ? setSelectedRef : undefined}
                        optionId={optionIdFor(index)}
                        icon={getIcon(definition)}
                        label={definition.name}
                        isSelected={index === selectedIndex}
                        onSelect={() => onSelectIndex(index)}
                        onConfirm={() => onConfirmIndex(index)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...listboxProps}>
      {groups.map((group) => (
        <div key={group.key}>
          {groupHeader(group, true)}
          <div className="flex flex-col gap-[2px]">
            {group.items.map((definition, offset) => {
              const index = group.startIndex + offset;
              return (
                <PiBrowseListRow
                  key={`${group.key}-${definition.name}`}
                  ref={index === selectedIndex ? setSelectedRef : undefined}
                  optionId={optionIdFor(index)}
                  icon={getIcon(definition)}
                  title={definition.name}
                  subtitle={rowSubtitle(definition)}
                  isSelected={index === selectedIndex}
                  onSelect={() => onSelectIndex(index)}
                  onConfirm={() => onConfirmIndex(index)}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
